import React, { FC, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetCountryListForDropdowns } from "../../../../api/services/client/clientApis";
import { useGetMarketsQuery } from "../../../../api/services/mdmOffice/mdmOfficeApis";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  MuiButtonPrimary,
  MuiButtonSecondary,
  MuiGrid,
  MuiSelect,
  MuiSelectWithTextInput,
  SearchSaveFiltersSubHeader,
} from "../../../../components";
import { MAX_LENGTH_75 } from "../../../../helpers/constants";
import { setApiDataToDropDownOption } from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useMediaQueries } from "../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { useToast } from "../../../../hooks/useToast";
import { toggleLoader } from "../../../../redux/slices/appState/appStateSlice";
import { mdmSearchOfficeSiteCoreKeys } from "../../../mdmCreateOffice/siteCore/SiteCoreKeys";
import { OfficeSearchFormKey } from "../../modelsAndSchemas/models";
import { MdmOfficeSearchFormWrapper } from "./MdmOfficeSearchForm.style";

interface OfficeSearchFormProps {
  handleClearAllSearch: () => void;
  handleSearchClick: () => void;
  handleSaveFilter: () => void;
  refetchListOfFilters: () => void;
  handleSavedFilter: () => void;
  isValidForm: boolean;
  disableActionBtn: boolean;
  anchorRef?: React.RefObject<HTMLButtonElement>;
}

export const MdmOfficeSearchForm: FC<OfficeSearchFormProps> = (props) => {
  const {
    handleClearAllSearch,
    handleSearchClick,
    disableActionBtn,
    anchorRef,
    handleSaveFilter,
    handleSavedFilter,
    isValidForm,
  } = props;

  const pageId = "mdm-office-search-form";

  const { gridSizeLarge: gridSize, gridSizeLargeClass } = useGridContainer();

  const { control } = useFormContext();

  const { isMobile } = useMediaQueries();

  const { fetchLabel, commonLabels, fetchComponentTitle, fetchDropdownValues, commonValidationMessages } =
    useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const { displayToast } = useToast();
  const dispatch = useDispatch();

  const { data: countryListData } = useGetCountryListForDropdowns();

  const {
    data: marketData,
    isLoading: isLoadingMarkets,
    isError: isErrorMarkets,
  } = useGetMarketsQuery();

  useEffect(() => {
    if (isErrorMarkets) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
    }
  }, [isErrorMarkets]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isLoadingOfficeData: {
          isVisible: isLoadingMarkets,
          message: commonLabels.loadingPleaseWait,
        },
      }),
    );
    return () => {
      dispatch(toggleLoader({ isLoadingOfficeData: { isVisible: false } }));
    };
  }, [isLoadingMarkets]);

  const renderMdmOfficeHeader = () => {
    return (
      <SearchSaveFiltersSubHeader
        onSavedFilterClick={handleSavedFilter}
        onResetAllClick={handleClearAllSearch}
        anchorRef={anchorRef}
        isSavedFilterDisabled={disableActionBtn}
        resetButtonTestId={`${pageId}-clearAll`}
        savedFilterTestId={`${pageId}-savedFilter`}
      />
    );
  };

  return (
    <MdmOfficeSearchFormWrapper>
      <AccordionHeader
        title={
          <AccordionHeaderText
            label={fetchComponentTitle(
              mdmSearchOfficeSiteCoreKeys.searchOfficeTitle,
            )}
          />
        }
        isExpanded={!isMobile}
      >
        {renderMdmOfficeHeader()}
        <form data-testid="Office-form">
          <MuiGrid container className="form-container">
            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={OfficeSearchFormKey.officeName}
              >
                <MuiSelectWithTextInput
                  options={fetchDropdownValues(
                    mdmSearchOfficeSiteCoreKeys.searchOfficeTitle,
                    mdmSearchOfficeSiteCoreKeys.filterByList,
                  )}
                  label={fetchLabel(
                    mdmSearchOfficeSiteCoreKeys.searchOfficeTitle,
                    mdmSearchOfficeSiteCoreKeys.officeName,
                  )}
                  placeholder={commonLabels.inputPlaceHolder}
                  control={control}
                  selectBoxName={OfficeSearchFormKey.officeNameFilter}
                  id={`${pageId}-${OfficeSearchFormKey.officeName}`}
                  selectBoxId={`${pageId}-${OfficeSearchFormKey.officeNameFilter}`}
                  maxLength={MAX_LENGTH_75}
                  data-testid={`${pageId}-${OfficeSearchFormKey.officeName}`}
                  requiredARIALabel={commonARIALabel.required}
                  tooltipMessage={
                    commonValidationMessages.fieldSpecialCharactersTooltipMessage
                  }
                  tooltipHeaderTitle={
                    commonValidationMessages.allowedSpecialCharactersTitle
                  }
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={OfficeSearchFormKey.parentOffice}
              >
                <MuiSelectWithTextInput
                  options={fetchDropdownValues(
                    mdmSearchOfficeSiteCoreKeys.searchOfficeTitle,
                    mdmSearchOfficeSiteCoreKeys.filterByList,
                  )}
                  label={fetchLabel(
                    mdmSearchOfficeSiteCoreKeys.searchOfficeTitle,
                    mdmSearchOfficeSiteCoreKeys.parentOffice,
                  )}
                  placeholder={commonLabels.inputPlaceHolder}
                  control={control}
                  selectBoxName={OfficeSearchFormKey.parentOfficeFilter}
                  id={`${pageId}-${OfficeSearchFormKey.parentOffice}`}
                  selectBoxId={`${pageId}-${OfficeSearchFormKey.parentOfficeFilter}`}
                  maxLength={MAX_LENGTH_75}
                  data-testid={`${pageId}-${OfficeSearchFormKey.parentOffice}`}
                  requiredARIALabel={commonARIALabel.required}
                  tooltipMessage={
                    commonValidationMessages.fieldSpecialCharactersTooltipMessage
                  }
                  tooltipHeaderTitle={
                    commonValidationMessages.allowedSpecialCharactersTitle
                  }
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={OfficeSearchFormKey.officeType}
              >
                <MuiSelect
                  label={fetchLabel(
                    mdmSearchOfficeSiteCoreKeys.searchOfficeTitle,
                    mdmSearchOfficeSiteCoreKeys.officeType,
                  )}
                  options={fetchDropdownValues(
                    mdmSearchOfficeSiteCoreKeys.searchOfficeTitle,
                    mdmSearchOfficeSiteCoreKeys.officeTypeList,
                  )}
                  value=""
                  id={`${pageId}-${OfficeSearchFormKey.officeType}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  visibleDefaultSelectOption
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={OfficeSearchFormKey.marketId}
              >
                <MuiSelect
                  label={fetchLabel(
                    mdmSearchOfficeSiteCoreKeys.searchOfficeTitle,
                    mdmSearchOfficeSiteCoreKeys.market,
                  )}
                  options={setApiDataToDropDownOption(marketData)}
                  value=""
                  id={`${pageId}-${OfficeSearchFormKey.marketId}`}
                  visibleDefaultSelectOption
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={OfficeSearchFormKey.state}
              >
                <MuiSelectWithTextInput
                  label={fetchLabel(
                    mdmSearchOfficeSiteCoreKeys.searchOfficeTitle,
                    mdmSearchOfficeSiteCoreKeys.stateOrProvince,
                  )}
                  value=""
                  id={`${pageId}-${OfficeSearchFormKey.state}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  options={fetchDropdownValues(
                    mdmSearchOfficeSiteCoreKeys.searchOfficeTitle,
                    mdmSearchOfficeSiteCoreKeys.filterByList,
                  )}
                  control={control}
                  selectBoxName={OfficeSearchFormKey.stateFilter}
                  selectBoxId={`${pageId}-${OfficeSearchFormKey.stateFilter}`}
                  maxLength={MAX_LENGTH_75}
                  data-testid={`${pageId}-${OfficeSearchFormKey.state}`}
                  requiredARIALabel={commonARIALabel.required}
                  tooltipMessage={
                    commonValidationMessages.fieldSpecialCharactersTooltipMessage
                  }
                  tooltipHeaderTitle={
                    commonValidationMessages.allowedSpecialCharactersTitle
                  }
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController control={control} name={OfficeSearchFormKey.city}>
                <MuiSelectWithTextInput
                  label={fetchLabel(
                    mdmSearchOfficeSiteCoreKeys.searchOfficeTitle,
                    mdmSearchOfficeSiteCoreKeys.city,
                  )}
                  value=""
                  id={`${pageId}-${OfficeSearchFormKey.city}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  options={fetchDropdownValues(
                    mdmSearchOfficeSiteCoreKeys.searchOfficeTitle,
                    mdmSearchOfficeSiteCoreKeys.filterByList,
                  )}
                  control={control}
                  selectBoxName={OfficeSearchFormKey.cityFilter}
                  selectBoxId={`${pageId}-${OfficeSearchFormKey.cityFilter}`}
                  maxLength={MAX_LENGTH_75}
                  data-testid={`${pageId}-${OfficeSearchFormKey.city}`}
                  requiredARIALabel={commonARIALabel.required}
                  tooltipMessage={
                    commonValidationMessages.fieldSpecialCharactersTooltipMessage
                  }
                  tooltipHeaderTitle={
                    commonValidationMessages.allowedSpecialCharactersTitle
                  }
                />
              </FormController>
            </MuiGrid>
            <MuiGrid
              size={gridSize}
              className={gridSizeLargeClass}
              spacing={isMobile ? 2 : 0}
            >
              <FormController
                control={control}
                name={OfficeSearchFormKey.countryId}
              >
                <MuiSelect
                  label={fetchLabel(
                    mdmSearchOfficeSiteCoreKeys.searchOfficeTitle,
                    mdmSearchOfficeSiteCoreKeys.country,
                  )}
                  options={setApiDataToDropDownOption(countryListData, true)}
                  value=""
                  id={`${pageId}-${OfficeSearchFormKey.countryId}`}
                  visibleDefaultSelectOption
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                  showSearch
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={OfficeSearchFormKey.status}
              >
                <MuiSelect
                  label={fetchLabel(
                    mdmSearchOfficeSiteCoreKeys.searchOfficeTitle,
                    mdmSearchOfficeSiteCoreKeys.status,
                  )}
                  options={fetchDropdownValues(
                    mdmSearchOfficeSiteCoreKeys.searchOfficeTitle,
                    mdmSearchOfficeSiteCoreKeys.statusList,
                  )}
                  value=""
                  id={`${pageId}-${OfficeSearchFormKey.status}`}
                  visibleDefaultSelectOption
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>
          <MuiGrid container className="button-container">
            <MuiButtonSecondary
              onClick={handleSaveFilter}
              id={`${pageId}-saveFilter`}
              disabled={!isValidForm}
            >
              {commonLabels.saveFilter}
            </MuiButtonSecondary>
            <MuiButtonPrimary
              onClick={handleSearchClick}
              id={`${pageId}-search`}
            >
              {commonLabels.search}
            </MuiButtonPrimary>
          </MuiGrid>
        </form>
      </AccordionHeader>
    </MdmOfficeSearchFormWrapper>
  );
};
